import { useTranslation } from 'react-i18next';
import { resolveLocalizedName, type SessionMetadataCollect } from 'pi-kiosk-shared';
import { PickupSlotPicker } from '../PickupSlotPicker.js';
import { CollectPickupSheetRetail } from './CollectPickupSheetRetail.js';
import type { CollectStepSheetProps } from './collectStepSheetTypes.js';
import { SelfCollectConfirmToggle } from './SelfCollectConfirmToggle.js';
import {
  resolveCollectSheetDisplayTiming,
  shouldShowNowTimingUnderLaterLock,
} from '../../shop/presenters/shopCheckoutPresenter.js';

export type { CollectStepSheetProps } from './collectStepSheetTypes.js';

export function CollectStepSheet({
  usePickupCopyKeys = false,
  hideHeader = false,
  ...props
}: CollectStepSheetProps): JSX.Element {
  if (usePickupCopyKeys) {
    return <CollectPickupSheetRetail {...props} hideHeader={hideHeader} />;
  }

  return <CollectStepSheetLegacy {...props} />;
}

function CollectStepSheetLegacy({
  kioskId,
  points,
  pointsLoading,
  pointsLoadError = false,
  onPointsRetry,
  collect,
  scheduledPickupEnabled,
  pickupSlot,
  onPickupSlotChange,
  onCollectChange,
  lockedCollectTiming,
  allowedPickupHandoffModes = [],
  selectedPickupHandoffMode = null,
  onPickupHandoffModeChange,
  selfCollectOnlyGate = false,
  selfCollectConfirmed = false,
  onSelfCollectConfirmedChange,
  selfCollectConfirmAtKiosk = false,
  payOnSpotProximityNoticeMinutes,
}: Omit<CollectStepSheetProps, 'usePickupCopyKeys' | 'hideHeader'>): JSX.Element {
  const { t, i18n } = useTranslation('customer');
  const selfCollectLabel = selfCollectConfirmAtKiosk
    ? t('pickup.selfCollectConfirm.label')
    : t('pickup.selfCollectConfirm.onSiteLabel');
  const selfCollectHelper = selfCollectConfirmAtKiosk
    ? t('pickup.selfCollectConfirm.helper')
    : t('pickup.selfCollectConfirm.onSiteHelper');
  const titleKey = 'checkout.collectStep.title';
  const introKey = 'checkout.collectStep.intro';
  const timingLegendKey = 'checkout.collectStep.timingLegend';
  const handoffLegendKey = 'checkout.collectStep.handoffLegend';
  const pointLegendKey = 'checkout.collectStep.pointLegend';
  const timing = resolveCollectSheetDisplayTiming({ lockedCollectTiming, collect });
  const selectedPointId = collect?.pickupPointId ?? null;
  const showHandoffPicker =
    allowedPickupHandoffModes.length > 1 && onPickupHandoffModeChange !== undefined;
  const effectiveHandoffMode =
    selectedPickupHandoffMode ??
    (allowedPickupHandoffModes.length === 1 ? allowedPickupHandoffModes[0] : null);
  const preserveUsableNow = shouldShowNowTimingUnderLaterLock(lockedCollectTiming, collect);

  const selectPoint = (pickupPointId: number): void => {
    onCollectChange({
      timing,
      pickupPointId,
      ...(timing === 'LATER' && collect?.pickup !== undefined ? { pickup: collect.pickup } : {}),
    });
  };

  const selectTiming = (nextTiming: SessionMetadataCollect['timing']): void => {
    if (
      lockedCollectTiming !== undefined &&
      nextTiming !== lockedCollectTiming &&
      !(preserveUsableNow && nextTiming === 'NOW')
    ) {
      return;
    }
    if (selectedPointId === null) {
      const defaultPoint = points.find((p) => p.isOnSpot) ?? points[0];
      if (defaultPoint === undefined) {
        return;
      }
      onCollectChange({
        timing: nextTiming,
        pickupPointId: defaultPoint.id,
      });
      if (nextTiming === 'NOW') {
        onPickupSlotChange(null);
      }
      return;
    }
    onCollectChange({
      timing: nextTiming,
      pickupPointId: selectedPointId,
      ...(nextTiming === 'LATER' && pickupSlot
        ? {
            pickup: {
              promisedPickupAt: pickupSlot.startAt,
              pickupWindowEndAt: pickupSlot.endAt,
            },
          }
        : {}),
    });
    if (nextTiming === 'NOW') {
      onPickupSlotChange(null);
    }
  };

  if (pointsLoading) {
    return (
      <section data-testid="collect-step-sheet">
        <p className="text-sm text-[var(--color-on-surface-muted)]">{t('checkout.collectStep.loading')}</p>
      </section>
    );
  }

  // G1/G7 — Prefer selectable points (incl. SP on-spot fallback) over loadError/noPoints.
  if (points.length === 0) {
    if (pointsLoadError) {
      return (
        <section data-testid="collect-step-sheet" className="flex flex-col gap-3">
          <p className="text-sm text-[var(--color-danger)]" role="alert">
            {t('checkout.collectStep.loadError')}
          </p>
          {onPointsRetry !== undefined ? (
            <button
              type="button"
              className="self-start text-sm font-medium text-[var(--color-accent)] underline underline-offset-2"
              data-testid="collect-step-points-retry"
              onClick={onPointsRetry}
            >
              {t('shared.retry')}
            </button>
          ) : null}
        </section>
      );
    }
    return (
      <section data-testid="collect-step-sheet">
        <p className="text-sm text-[var(--color-danger)]" role="alert">
          {t('checkout.collectStep.noPoints')}
        </p>
      </section>
    );
  }

  const showNowTiming =
    lockedCollectTiming === undefined || lockedCollectTiming === 'NOW' || preserveUsableNow;
  const showLaterTiming =
    !preserveUsableNow &&
    (lockedCollectTiming === undefined || lockedCollectTiming === 'LATER') &&
    scheduledPickupEnabled;
  const showTimingFieldset = showNowTiming || showLaterTiming;

  return (
    <section data-testid="collect-step-sheet" className="flex w-full min-w-0 flex-col gap-4">
      {onPointsRetry !== undefined ? (
        <button
          type="button"
          className="self-start text-xs font-medium text-[var(--color-on-surface-muted)] underline underline-offset-2"
          data-testid="collect-step-points-retry"
          onClick={onPointsRetry}
        >
          {t('shared.retry')}
        </button>
      ) : null}
      <div>
        <p className="text-base font-semibold text-[var(--color-on-surface)]">{t(titleKey)}</p>
        <p className="mt-1 text-sm text-[var(--color-on-surface-muted)]">{t(introKey)}</p>
      </div>

      {showTimingFieldset ? (
        <fieldset className="flex flex-col gap-2" data-testid="collect-timing-fieldset">
          <legend className="text-sm font-medium text-[var(--color-on-surface)]">
            {t(timingLegendKey)}
          </legend>
          {showNowTiming ? (
            <label className="flex items-center gap-2 text-sm">
              <input
                type="radio"
                name="collect-timing"
                checked={timing === 'NOW'}
                onChange={() => selectTiming('NOW')}
              />
              {t('checkout.collectStep.timingNow')}
            </label>
          ) : null}
          {showLaterTiming ? (
            <label className="flex items-center gap-2 text-sm">
              <input
                type="radio"
                name="collect-timing"
                checked={timing === 'LATER'}
                onChange={() => selectTiming('LATER')}
              />
              {t('checkout.collectStep.timingLater')}
            </label>
          ) : null}
        </fieldset>
      ) : null}

      {showHandoffPicker ? (
        <fieldset className="flex flex-col gap-2" data-testid="collect-handoff-fieldset">
          <legend className="text-sm font-medium text-[var(--color-on-surface)]">
            {t(handoffLegendKey)}
          </legend>
          {allowedPickupHandoffModes.map((mode) => (
            <label key={mode} htmlFor={`collect-handoff-${mode}`} className="flex items-center gap-2 text-sm">
              <input
                id={`collect-handoff-${mode}`}
                type="radio"
                name="collect-handoff-mode"
                checked={effectiveHandoffMode === mode}
                onChange={() => onPickupHandoffModeChange?.(mode)}
              />
              {t(`checkout.handoffMode.${mode}`)}
            </label>
          ))}
        </fieldset>
      ) : null}

      <fieldset className="flex flex-col gap-2">
        <legend className="text-sm font-medium text-[var(--color-on-surface)]">
          {t(pointLegendKey)}
        </legend>
        {points.map((point) => (
          <label key={point.id} className="flex items-start gap-2 rounded-lg border border-[var(--color-border)] p-3 text-sm">
            <input
              type="radio"
              name="pickup-point"
              checked={selectedPointId === point.id}
              onChange={() => selectPoint(point.id)}
            />
            <span>
              <span className="font-medium text-[var(--color-on-surface)]">
                {resolveLocalizedName(point.name, point.nameLocales, i18n.language)}
              </span>
              {point.isOnSpot ? (
                <span className="ml-2 text-xs text-[var(--color-on-surface-muted)]">
                  {t('checkout.collectStep.onSpot')}
                </span>
              ) : null}
            </span>
          </label>
        ))}
      </fieldset>

      {timing === 'LATER' && scheduledPickupEnabled && selectedPointId !== null ? (
        <div className="flex w-full min-w-0 flex-col gap-3" data-testid="scheduled-pickup-section">
          <div>
            <p className="text-base font-semibold text-[var(--color-on-surface)]">
              {t('checkout.scheduledPickupTitle')}
            </p>
            <p className="mt-1 text-sm text-[var(--color-on-surface-muted)]">
              {t('checkout.scheduledPickupLegend')}
            </p>
          </div>
          <PickupSlotPicker
            kioskId={kioskId}
            pickupPointId={selectedPointId}
            value={pickupSlot}
            onChange={(slot) => {
              onPickupSlotChange(slot);
              if (selectedPointId === null) {
                return;
              }
              if (slot !== null) {
                onCollectChange({
                  timing: 'LATER',
                  pickupPointId: selectedPointId,
                  pickup: {
                    promisedPickupAt: slot.startAt,
                    pickupWindowEndAt: slot.endAt,
                  },
                });
                return;
              }
              // FIX 4.1: day mismatch clears slot → omit pickup so Continue stays gated
              onCollectChange({
                timing: 'LATER',
                pickupPointId: selectedPointId,
              });
            }}
          />
        </div>
      ) : null}

      {selfCollectOnlyGate && onSelfCollectConfirmedChange !== undefined ? (
        <SelfCollectConfirmToggle
          label={selfCollectLabel}
          helper={selfCollectHelper}
          checked={selfCollectConfirmed}
          onCheckedChange={onSelfCollectConfirmedChange}
        />
      ) : null}

      {payOnSpotProximityNoticeMinutes !== undefined &&
      payOnSpotProximityNoticeMinutes > 0 &&
      timing === 'NOW' ? (
        <div
          className="rounded-md border border-[var(--color-border)] bg-[var(--color-surface-elevated)] px-3 py-3"
          role="status"
        >
          <p className="text-sm text-[var(--color-on-surface)]">
            {t('pickup.slaNotice', { minutes: payOnSpotProximityNoticeMinutes })}
          </p>
        </div>
      ) : null}
    </section>
  );
}
